import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
    user: null,
    loading: true,
    refresh: async () => {},
    logout: async () => {},
});

export function AuthProvider({ children }) {
    // Lazily compute initial loading state to avoid set-state-in-effect for the OAuth-callback skip case.
    const isOAuthCallback = typeof window !== "undefined" && window.location.hash?.includes("session_id=");
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(!isOAuthCallback);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me", { withCredentials: true });
            setUser(data);
        } catch (e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id first.
        if (isOAuthCallback) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/me", { withCredentials: true });
                if (!cancelled) setUser(data);
            } catch (e) {
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOAuthCallback]);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout", {}, { withCredentials: true });
        } catch (e) {
            // ignore network errors on logout
        }
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, refresh, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
